/**
 * /prototypes/manwha — Manwha Videos prototype (watch-list + pipeline status).
 *
 * Catalog over vm_Scratch (kinds: manwha-series, manwha-chapter). The heavy media
 * pipeline (scrape → Gemini vision → dub → ffmpeg scroll-render) runs in the
 * `kit-manwha-video` skill on a local machine — it can't run in this CF Worker.
 * Adding a chapter here just QUEUES it (status=pending); the skill's local
 * render-bridge (`kit-manwha-video render-pending`) picks up pending rows, runs the
 * pipeline, and `publish` upserts the row with its R2 videoUrl + counts. This page
 * lists series/chapters, shows pipeline status, and plays finished videos. Admin only.
 */
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  toast,
} from "@aix/ui"
import { Delete02Icon } from "@hugeicons-pro/core-solid-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import { EManwhaStatus } from "@aix/vidmax-shared"
import type { ManwhaChapter, ManwhaSeries } from "@aix/vidmax-shared"
import { reportErrorToSentry } from "@aix/webutils/src/sentry"
import CustomHead from "~/components/SEOHead/CustomHead"
import { PrototypeLayout } from "~/layouts/PrototypeLayout"
import { api } from "~/utils/api"

/** Parse an asurascan chapter reader URL → { slug, chapter }. */
function parseChapterUrl(url: string): { slug: string; chapter: string } | null {
  const m = /\/comics\/([^/]+)\/chapter\/([^/?#]+)/.exec(url)
  return m ? { slug: m[1], chapter: m[2] } : null
}
function titleFromSlug(slug: string): string {
  return slug
    .replace(/-[0-9a-f]{6,}$/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const STATUS_VARIANT: Record<EManwhaStatus, "default" | "secondary" | "outline" | "destructive"> = {
  [EManwhaStatus.PENDING]: "outline",
  [EManwhaStatus.INGESTED]: "secondary",
  [EManwhaStatus.ANALYZED]: "secondary",
  [EManwhaStatus.DUBBED]: "secondary",
  [EManwhaStatus.RENDERED]: "default",
  [EManwhaStatus.ERROR]: "destructive",
}

function AddChapterDialog() {
  const utils = api.useUtils()
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState("")
  const [title, setTitle] = useState("")

  const parsed = useMemo(() => parseChapterUrl(url.trim()), [url])
  const { data: series } = api.scratch.list.useQuery({ kind: "manwha-series" })

  const upsert = api.scratch.upsert.useMutation()

  const submit = async () => {
    if (!parsed) {
      toast.error("Couldn't parse a /comics/<slug>/chapter/<n> URL")
      return
    }
    try {
      // ensure the series exists (one row per slug)
      const hasSeries = (series ?? []).some((s) => (s.data as ManwhaSeries).slug === parsed.slug)
      if (!hasSeries) {
        const seriesData: ManwhaSeries = {
          slug: parsed.slug,
          title: title.trim() || titleFromSlug(parsed.slug),
          sourceUrl: url.trim().split("/chapter/")[0],
        }
        await upsert.mutateAsync({ kind: "manwha-series", data: seriesData })
      }
      const chapterData: ManwhaChapter = {
        seriesSlug: parsed.slug,
        chapter: parsed.chapter,
        sourceUrl: url.trim(),
        status: EManwhaStatus.PENDING,
      }
      await upsert.mutateAsync({ kind: "manwha-chapter", data: chapterData })
      toast.success(`Added ${titleFromSlug(parsed.slug)} ch.${parsed.chapter}`)
      setOpen(false)
      setUrl("")
      setTitle("")
      void utils.scratch.list.invalidate()
    } catch (e) {
      void reportErrorToSentry(e, "manwha-add-chapter")
      toast.error((e as Error).message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-chapter-btn">
          Add Chapter
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a manwha chapter</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1">
            <Label>Chapter URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://asurascans.com/comics/<slug>/chapter/63"
              data-testid="chapter-url"
            />
            {url.trim() && !parsed && <p className="text-destructive text-xs">Not a valid chapter URL.</p>}
            {parsed && (
              <p className="text-muted-foreground text-xs">
                {titleFromSlug(parsed.slug)} · chapter {parsed.chapter}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Series title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Derived from slug if blank" />
          </div>
          <Button className="w-full" disabled={!parsed || upsert.isLoading} onClick={() => void submit()} data-testid="add-chapter-submit">
            {upsert.isLoading ? "Adding…" : "Add chapter"}
          </Button>
          <p className="text-muted-foreground text-xs">
            Queues the chapter (status <span className="font-medium">pending</span>). The local render-bridge{" "}
            <code className="bg-muted rounded px-1">kit-manwha-video render-pending</code> picks it up, runs the pipeline,
            and publishes the finished video back here.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function DeleteButton({ id, label }: { id: string; label: string }) {
  const utils = api.useUtils()
  const [armed, setArmed] = useState(false)
  const del = api.scratch.remove.useMutation({
    onSuccess: () => {
      toast.success(`Deleted ${label}`)
      void utils.scratch.list.invalidate()
    },
    onError: (e) => toast.error(e.message),
  })
  return (
    <Button
      size="icon"
      variant="ghost"
      className="text-muted-foreground hover:text-destructive absolute right-2 top-2 z-10 h-7 w-7"
      aria-label={`Double-click to delete ${label}`}
      onDoubleClick={() => del.mutate({ id: id as never })}
      data-testid="delete-chapter-btn"
    >
      {armed ? <span className="text-xs">…</span> : <HugeiconsIcon icon={Delete02Icon} size={15} onClick={() => setArmed(true)} />}
    </Button>
  )
}

function ChapterCard({ id, data }: { id: string; data: ManwhaChapter }) {
  return (
    <div className="border-border relative rounded-lg border p-4">
      <DeleteButton id={id} label={`ch.${data.chapter}`} />
      <div className="flex items-center justify-between gap-2 pr-7">
        <span className="font-medium">Chapter {data.chapter}</span>
        <Badge variant={STATUS_VARIANT[data.status] ?? "outline"}>{data.status}</Badge>
      </div>
      <div className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 text-xs">
        {data.pageCount != null && <span>{data.pageCount} pages</span>}
        {data.lineCount != null && <span>{data.lineCount} lines</span>}
        {data.durationSec != null && <span>{Math.round(data.durationSec)}s</span>}
      </div>
      {data.error && <p className="text-destructive mt-2 text-xs">{data.error}</p>}
      {data.videoUrl ? (
        <video controls src={data.videoUrl} className="mt-3 w-full rounded-md" />
      ) : (
        <a href={data.sourceUrl} target="_blank" rel="noreferrer" className="text-primary mt-3 inline-block text-xs underline">
          Open source chapter ↗
        </a>
      )}
    </div>
  )
}

function ManwhaListPage() {
  const { data: chapters, isLoading } = api.scratch.list.useQuery({ kind: "manwha-chapter" })
  const { data: series } = api.scratch.list.useQuery({ kind: "manwha-series" })

  // group chapters by series slug, sorted by chapter number desc
  const grouped = useMemo(() => {
    const seriesBySlug = new Map<string, ManwhaSeries>(
      (series ?? []).map((s) => [(s.data as ManwhaSeries).slug, s.data as ManwhaSeries] as const),
    )
    const bySlug = new Map<string, { id: string; data: ManwhaChapter }[]>()
    for (const row of chapters ?? []) {
      const d = row.data as ManwhaChapter
      if (!bySlug.has(d.seriesSlug)) bySlug.set(d.seriesSlug, [])
      bySlug.get(d.seriesSlug)!.push({ id: row.id, data: d })
    }
    return [...bySlug.entries()].map(([slug, chs]) => ({
      slug,
      title: seriesBySlug.get(slug)?.title ?? titleFromSlug(slug),
      chapters: chs.sort((a, b) => Number(b.data.chapter) - Number(a.data.chapter)),
    }))
  }, [chapters, series])

  return (
    <>
      <CustomHead title="Manwha Videos" />
      <div className="container mx-auto space-y-6 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Manwha Videos</h1>
            <p className="text-muted-foreground text-sm">
              Watch-list of manwha chapters turned into dubbed scroll-videos (kit-manwha-video). Add a chapter, run the
              skill, watch it here.
            </p>
          </div>
          <AddChapterDialog />
        </div>

        {isLoading ? (
          <div className="text-muted-foreground py-12 text-center">Loading…</div>
        ) : !grouped.length ? (
          <div className="text-muted-foreground py-12 text-center" data-testid="empty-manwha">
            No chapters yet — add one to start.
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map((g) => (
              <div key={g.slug} className="space-y-3">
                <h2 className="text-lg font-semibold">{g.title}</h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {g.chapters.map((c) => (
                    <ChapterCard key={c.id} id={c.id} data={c.data} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function ManwhaListWrapper() {
  return (
    <PrototypeLayout>
      <ManwhaListPage />
    </PrototypeLayout>
  )
}

export const Route = createFileRoute("/prototypes/manwha/")({
  component: ManwhaListWrapper,
})
